import { Location } from "@angular/common";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { NgbModal, NgbTypeahead } from "@ng-bootstrap/ng-bootstrap";
import { DateTime, Duration } from "luxon";
import {
    BehaviorSubject,
    combineLatest,
    interval,
    merge,
    Observable,
    of,
    Subject
} from "rxjs";
import {
    catchError,
    debounce,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    tap
} from "rxjs/operators";
import { IProject } from "src/app/admin/projects/shared/models/IProject";
import { ProjectService } from "src/app/admin/shared/services/project.service";
import { UsersService } from 'src/app/admin/users/users.service';
import { IWorkOrder } from "src/app/admin/work-orders/models/IWorkOrder";
import { WorkOrderService } from "src/app/admin/work-orders/work-order.service";
import { Timer } from "src/app/shared/components/timers/timer";
import { TimerService } from "src/app/shared/services/timer.service";
import { TimePeriodsService } from "src/app/time-periods/time-periods.service";
import { IUser } from "../../admin/users/models/IUser";
import { ITimeEntry } from "../models/ITimeEntry";
import { TimetrackingService } from "../timetracking.service";
import { TimeAddService } from "./time-add.service";


@Component({
  selector: "app-time-add",
  templateUrl: "./time-add.component.html",
  styleUrls: ["./time-add.component.scss"],
})
export class TimeAddComponent implements OnInit {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private savingSubject = new BehaviorSubject<boolean>(false);


  loading$ = this.loadingSubject.asObservable();
  saving$ = this.savingSubject.asObservable();

  @ViewChild("projectSearch", { static: false }) projectSearch: NgbTypeahead;
  @ViewChild("creating", { static: false }) creatingModal: ElementRef;

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  affectedTimeEntries: string[] = [];

  workDay: DateTime;
  userId: number; // TODO if we are passing this in, we want to make sure not everyone can just edit everyones time
  startingProjectId: number;

  allActiveProjects: IProject[] = [];
  recentActiveProjects: IProject[] = [];
  projectWorkOrders: IWorkOrder[] = [];

  timeEntryForm: FormGroup;
  manuallyChooseWorkOrder = false;
  submitAttempted = false;

  fromTimer = false;
  activeTimer: Timer = null;
  userProfile: IUser;

  get projectControl() {
    return this.timeEntryForm.controls.project;
  }
  get workOrderControl() {
    return this.timeEntryForm.controls.workOrder;
  }
  get workDayControl() {
    return this.timeEntryForm.controls.workDay;
  }
  get forThirdPartyControl() {
    return this.timeEntryForm.controls.forThirdParty;
  }
  get hoursControl() {
    return this.timeEntryForm.controls.hours;
  }
  get minutesControl() {
    return this.timeEntryForm.controls.minutes;
  }
  get totalMinutesControl() {
    return this.timeEntryForm.controls.totalMinutes;
  }
  get manuallyChooseControl() {
    return this.timeEntryForm.controls.manuallyChooseWorkOrder;
  }

  constructor(
    activatedRoute: ActivatedRoute,
    private timerService: TimerService,
    private fb: FormBuilder,
    private timeTrackingService: TimetrackingService,
    private projectService: ProjectService,
    private workOrderService: WorkOrderService,
    private timePeriodService: TimePeriodsService,
    private location: Location,
    private modalService: NgbModal,
    private timeAddService: TimeAddService,
    private usersService: UsersService,
  ) {
    this.userId = parseInt(activatedRoute.snapshot.queryParams.userid, 10);
    this.startingProjectId = parseInt(activatedRoute.snapshot.queryParams.projectid, 10) || null;
    const workdayString = activatedRoute.snapshot.queryParams.workday;
    this.fromTimer = !!activatedRoute.snapshot.queryParams.fromTimer;


    if (workdayString) {
      this.workDay = DateTime.fromISO(
        activatedRoute.snapshot.queryParams.workday
      );
    } else {
      this.workDay = DateTime.local();
    }
  }

  ngOnInit() {
    this.createForm();
    this.wireProjectValueChanges();

    const startingProjectId$ = this.fromTimer
      ? this.timerService.timer$.pipe(
        tap((timer) => {
          if (timer) {
            this.activeTimer = timer;
            this.workDay = timer.created;
            this.workDayControl.setValue(this.workDay.toISODate());
            // TODO make timer a class so that i can call "get minutes" or something instead of having to do this everywhere
            const currentMillis =
              DateTime.local().toMillis() -
              timer.start.toMillis() +
              timer.minutesTimed * 60 * 1000;
            const duration = Duration.fromMillis(currentMillis);
            const minutes = Math.floor(duration.as("minutes"));
            this.hoursControl.setValue(Math.floor(minutes / 60));
            this.minutesControl.setValue(minutes % 60);
          }
        }),
        map((timer) => timer.projectId)
      )
      : of(this.startingProjectId);

    combineLatest([
      this.projectService.getProjects(),
      this.projectService.getRecentProjects(this.userId),
      startingProjectId$,
    ]).subscribe(([projects, recentProjects, startingProjectId]) => {
      this.allActiveProjects = projects.filter((p) => p.active);
      this.recentActiveProjects = recentProjects.filter((p) => p.active);
      if (startingProjectId) {
        const selectedProject = this.allActiveProjects.find((project) => {
          return project.id === startingProjectId;
        });
        this.projectControl.setValue(selectedProject);
      }
    });

    const lastDateSelected = this.timeAddService.getLastDateSelected();
    const currentControlValue = this.workDayControl.value;

    if (!currentControlValue || isNaN(Date.parse(currentControlValue))) {
      this.workDayControl.setValue(lastDateSelected.toISODate());
    }

    this.workDayControl.valueChanges.subscribe(date => {
      this.timeAddService.setLastDateSelected(DateTime.fromISO(date));
    });
  }
  save() {
    this.submitAttempted = true;
    if (!this.timeEntryForm.valid) {
      this.timeEntryForm.markAllAsTouched();
      return;
    }
    const formValues = this.timeEntryForm.value;
    const selectedProject: IProject = formValues.project;
    const selectedWorkOrder: IWorkOrder = formValues.workOrder;
    const entry: ITimeEntry = {
      id: 0,
      userId: this.userId,
      entryDate: DateTime.local().toUTC(),
      entryDateIso: DateTime.local().toISO(),
      projectId: selectedProject.id,
      workOrderId: this.manuallyChooseControl.value
        ? selectedWorkOrder.id
        : null,
      workDay: formValues.workDay,
      workDayIso: formValues.workDay,
      forThirdParty: formValues.forThirdPartyControl,
      timeSpentMinutes: formValues.hours * 60 + formValues.minutes,
      isLocked: false,
      usesBuiltInTime: false,
    };
    this.savingSubject.next(true);
    this.timeTrackingService.saveTimeEntry(entry).subscribe(
      (response) => {
        this.affectedTimeEntries = response;
        if (this.activeTimer) {
          // TODO will this execute even as i navigate away?
          this.timerService.stopTimer();
        }
        this.createModal();
        this.goBack();
      },
      (error) => {
        this.savingSubject.next(false);
        alert(`Error saving time entry. Please try again or report to AD team.`);
      }
    );
  }
  cancel() {
    this.goBack();
  }
  goBack() {
    this.location.back();
  }
  // Time Form code that i would like to break out into its own component
  // -- i couldnt get control pending state to propogate up to this parent component when using async validation
  // -- so until then ill just keep the code here
  search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(
      debounceTime(200),
      distinctUntilChanged()
    );
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.projectSearch.isPopupOpen())
    );
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      // TODO when no term only show the top 10 favs?
      map((term) =>
        (term === ""
          ? this.allActiveProjects.slice(0, 10)
          : this.allActiveProjects.filter(
            (p) => p.name.toLowerCase().indexOf(term.toLowerCase()) > -1
          )
        ).slice(0, 10)
      )
    );
  };
  projectSearchFormatter(project: IProject) {
    return project.name;
  }
  private createForm() {
    this.timeEntryForm = this.fb.group(
      {
        project: [null, Validators.required],
        workOrder: "",
        workDay: [
          this.workDay.toISODate(),
          [Validators.required, this.notFutureDateValidator()],
          this.lockedTimePeriodAsyncValidator(),
        ],

        forThirdParty: [false],
        hours: [0],
        minutes: [0],
        totalMinutes: [],
        manuallyChooseWorkOrder: [false],
      },
      {
        validators: [this.PositiveTimeValidator()],
      }
    );
  }
  private notFutureDateValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const today = DateTime.local().startOf("day").toMillis();
      if (!control.value) {
        return { notFutureDate: { value: control.value } };
      }
      let selectedDate = DateTime.fromISO(control.value).toMillis();
      return selectedDate > today
        ? { notFutureDate: { value: control.value } }
        : null;
    };
  }
  private PositiveTimeValidator() {
    return (formGroup: FormGroup) => {
      const hoursControl = formGroup.controls.hours;
      const minutesControl = formGroup.controls.minutes;
      const totalMinutesControl = formGroup.controls.totalMinutes;

      const hours = parseInt(hoursControl.value, 10);
      const minutes = parseInt(minutesControl.value, 10);
      if ((hours <= 0 && minutes <= 0) || hours > 24 || minutes > 1440) {
        totalMinutesControl.setErrors({ positiveTime: true });
        return { notFutureDate: { value: true } };
      } else {
        // if we have other group errors this will remove them?
        totalMinutesControl.setErrors(null);
        return null;
      }
    };
  }
  private wireProjectValueChanges() {
    this.projectControl.valueChanges
      .pipe(debounce(() => interval(250)))
      .subscribe((selectedProject: IProject) => {
        if (selectedProject) {
          this.workOrderService
            .getWorkOrdersByProject(selectedProject.id)
            .subscribe((workOrders: IWorkOrder[]) => {
              this.projectWorkOrders = workOrders.filter(x => x.active);;
              let selectedWorkOrder = workOrders[0];
              this.workOrderControl.setValue(selectedWorkOrder);
            });
        }
      });
  }
  private lockedTimePeriodAsyncValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      // TODO can i be confident that the control has a value here because it went through the required validator first?
      const newDay = DateTime.fromISO(control.value).startOf("day");
      return this.timePeriodService
        .getTimePeriodExceptions(newDay, newDay)
        .pipe(
          map((timePeriods) => {
            // There should be only one time period for this one day
            const timePeriod = timePeriods[0];
            if (timePeriod) {
              const isLocked = !!timePeriod.locked;
              const hasException = !!timePeriod.timePeriodExceptions.find(
                (e) => e.userId === this.userId
              );
              return isLocked && !hasException
                ? { lockedTimePeriodAsync: true }
                : null;
            }
            return null;
          })
        );
    };
  }
  createModal() {
    if (this.affectedTimeEntries.length > 0) {
      this.modalService.open(this.creatingModal, {
        size: "lg",
        backdrop: "static",
        keyboard: false,
        scrollable: true,
      });
    }
  }
}
